// @flow
import * as React from 'react';
import idx from 'idx';
import { graphql } from 'react-apollo';
import { memoize } from 'lodash';
import update from 'immutability-helper';

import GET_UPLOADED_FILES_FOR_ORG_QUERY from './queries/GetUploadedFilesForOrg.gql';
import UPLOADED_FILES_SUBSCRIPTION from './queries/UploadedFilesSubscription.gql';

import type {
  AdminUploadedFiles,
  AdminUploadedFiles_organizations,
} from './queries/__generated__/AdminUploadedFiles';
import type { SubscribeToMoreFiles as SubscribeToMoreFilesType } from './queries/__generated__/SubscribeToMoreFiles';
import CenteredLoadingIndicator from 'components/CenteredLoadingIndicator';
import ErrorReporter from 'app/components/ErrorReporter';
import Row from 'components/Row';

type Props = {
  children: (props: {
    loadNextPage: () => void,
    renderLoading: ?React.Element,
    renderPaginatedLoading: ?React.Element,
    renderError: ?React.Element,
    organization: ?AdminUploadedFiles_organizations,
  }) => React.Node,
  data: ReactApolloData<AdminUploadedFiles>,
  viewingOrgUuid: string,
};

class AdminUploadedFilesContainer extends React.Component<Props, void> {
  componentDidMount() {
    this.subscribeToMoreFiles();
  }

  loadMoreFiles = memoize<[string, ?string], void>((viewingOrgUuid: string, cursor: ?string) => {
    this.props.data.fetchMore({
      variables: {
        uuid: viewingOrgUuid,
        cursor,
        limit: 20,
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if (!fetchMoreResult) {
          return previousResult;
        }

        const existingFiles =
          idx(previousResult, _ => _.organizations[0].autoMessageFileUploads.nodes) || [];
        const newFiles =
          idx(fetchMoreResult, _ => _.organizations[0].autoMessageFileUploads.nodes) || [];
        return update(previousResult, {
          organizations: {
            '0': {
              autoMessageFileUploads: {
                pageInfo: {
                  $set: fetchMoreResult.organizations[0].autoMessageFileUploads.pageInfo,
                },
                nodes: { $set: [...existingFiles, ...newFiles.slice(1)] },
              },
            },
          },
        });
      },
    });
  });

  loadNextPage = () => {
    const { data, viewingOrgUuid } = this.props;
    const pageInfo = idx(data, _ => _.organizations[0].autoMessageFileUploads.pageInfo);
    if (pageInfo && pageInfo.hasNextPage) {
      this.loadMoreFiles(viewingOrgUuid, pageInfo.endCursor);
    }
  };

  subscribeToMoreFiles = () => {
    const { data, viewingOrgUuid } = this.props;
    data.subscribeToMore({
      document: UPLOADED_FILES_SUBSCRIPTION,
      variables: { orgUuid: viewingOrgUuid },
      updateQuery: (
        prev,
        { subscriptionData }: { subscriptionData: { data: SubscribeToMoreFilesType } }
      ) => {
        const prevUploadedFiles = idx(prev, _ => _.organizations[0].autoMessageFileUploads.nodes);
        if (!subscriptionData || !prevUploadedFiles) {
          return prev;
        }

        const newFile = idx(subscriptionData, _ => _.data.watchAutoMessageFileUploads);
        if (!newFile || prevUploadedFiles.some(file => file.uuid === newFile.uuid)) {
          return prev;
        }

        return update(prev, {
          organizations: {
            '0': {
              autoMessageFileUploads: {
                nodes: { $set: [newFile, ...prevUploadedFiles] },
              },
            },
          },
        });
      },
    });
  };

  render() {
    const { data, children } = this.props;
    const organization = idx(data, _ => _.organizations[0]);
    const renderLoading = data.loading && !organization ? <CenteredLoadingIndicator /> : null;
    const renderPaginatedLoading =
      data.loading && organization ? (
        <Row>
          <CenteredLoadingIndicator size="small" />
        </Row>
      ) : null;
    const renderError =
      !data.loading && !organization ? (
        <ErrorReporter error="AdminUploadedFilesView - tried to render without an organization" />
      ) : null;
    return children({
      renderLoading,
      renderPaginatedLoading,
      renderError,
      organization,
      loadNextPage: this.loadNextPage,
    });
  }
}

const withUploadedFiles = graphql(GET_UPLOADED_FILES_FOR_ORG_QUERY, {
  options: ({ viewingOrgUuid }) => ({
    variables: { organizationUUID: viewingOrgUuid },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
  }),
});

export { AdminUploadedFilesContainer };

export default withUploadedFiles(AdminUploadedFilesContainer);
